import { Box, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { showFlashMessageWithTimeout } from "../../services/actions/flashMessage-actions";
import { NotificationManager } from "../../services/api/NotificationManager";
import { GetNotificationSettingsResponse } from "../../shared/payloads/notificationSettingsPayloads";
import { getCustomWebLogo } from "../../services/selectors/web-selectors";
import ActualVisitorsModal from "./ActualVisitorsModal";
import HeurekaNotificationModal from "./HeurekaNotificationModal";
import CustomNotificationModal from "./CustomNotificationModal";
import OrdersAndRegistrationsModal from "./OrdersAndRegistrationsModal";
import SummaryModal from "./SummaryModal";
import ZboziNotificationModal from "./ZboziNotificationModal";
import ShoptetLastOrdersNotificationModal from "./ShoptetLastOrdersNotificationModal";
import EshopRychleLastOrdersNotificationModal from "./EshopRychleLastOrdersNotificationModal";
import ShoptetCouponCodeNotificationModal from "./ShoptetCouponCodeNotificationModal";
import { useTranslation } from "react-i18next";
import { convertStringToLanguage } from "../../generic-utils";
import SpolehliveRecenzeNotificationModal from "./SpolehliveRecenzeNotificationModal";
import { useCampaign } from "../../context/CampaignContext";
import GoogleReviewNotificationModal from "./GoogleReviewNotificationModal";
import TrustPilotNotificationModal from "./TrustPilotNotificationModal";

const EditNotification = () => {
  const { notificationId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const customWebBranding = useSelector(getCustomWebLogo);
  const {
    activeSize,
    iconBackgroundColor,
    iconColor,
    iconBackgroundRadius,
    textColor,
    showIcon,
    backgroundColor,
    backgroundOpacity,
    borderRadius,
  } = useCampaign();

  const language = convertStringToLanguage(i18n.language);

  const [notificationToEdit, setNotificationToEdit] =
    useState<GetNotificationSettingsResponse>();
  const [
    ordersAndRegistrationsModalOpenFor,
    setOrdersAndRegistrationsModalOpenFor,
  ] = useState<"lastOrders" | "lastRegistrations" | undefined>(undefined);
  const [isActualVisitorsModalOpen, setIsActualVisitorsModalOpen] =
    useState<boolean>(false);
  const [summaryModalOpenedFor, setSummaryModalOpenedFor] = useState<
    "visitorSummary" | "registrationSummary" | "orderSummary" | undefined
  >();
  const [isHeurekaNotificationModalOpen, setIsHeurekaNotificationModalOpen] =
    useState<boolean>(false);
  const [
    isSpolehliveRecenzeNotificationModalOpen,
    setIsSpolehliveRecenzeNotificationModalOpen,
  ] = useState<boolean>(false);
  const [isZboziNotificationModalOpen, setIsZboziNotificationModalOpen] =
    useState<boolean>(false);
  const [isCustomNotificationModalOpen, setIsCustomNotificationModalOpen] =
    useState<boolean>(false);
  const [
    isShoptetLastOrdersNotificationModalOpen,
    setIsShoptetLastOrdersNotificationModalOpen,
  ] = useState<boolean>(false);
  const [
    isEshopRychleLastOrdersNotificationModalOpen,
    setIsEshopRychleLastOrdersNotificationModalOpen,
  ] = useState<boolean>(false);
  const [
    isShoptetCouponNotificationModalOpen,
    setIsShoptetCouponNotificationModalOpen,
  ] = useState<boolean>(false);
  const [
    isGoogleReviewNotificationModalOpen,
    setIsGoogleReviewNotificationModalOpen,
  ] = useState<boolean>(false);
  const [
    isTrustpilotReviewNotificationModalOpen,
    setIsTrustpilotReviewNotificationModalOpen,
  ] = useState<boolean>(false);

  useEffect(() => {
    const fetchNotification = async () => {
      if (notificationId) {
        try {
          const _notificationManager = NotificationManager.getManager();
          const result: GetNotificationSettingsResponse =
            await _notificationManager.getSingleNotificationSettingsAsync(
              notificationId,
            );
          setNotificationToEdit(result);
          if (
            result.type === "lastOrders" ||
            result.type === "lastRegistrations"
          ) {
            setOrdersAndRegistrationsModalOpenFor(result.type);
          }
          if (result.type === "actualVisitors") {
            setIsActualVisitorsModalOpen(true);
          }
          if (
            result.type === "visitorSummary" ||
            result.type === "registrationSummary" ||
            result.type === "orderSummary"
          ) {
            setSummaryModalOpenedFor(result.type);
          }
          if (result.type === "heureka") {
            setIsHeurekaNotificationModalOpen(true);
          }
          if (
            result.type === "zboziCompanyRating" ||
            result.type === "zboziProductRating"
          ) {
            setIsZboziNotificationModalOpen(true);
          }
          if (result.type === "individual") {
            setIsCustomNotificationModalOpen(true);
          }
          if (result.type === "shoptetLastOrders") {
            setIsShoptetLastOrdersNotificationModalOpen(true);
          }
          if (result.type === "eshopRychleLastOrders") {
            setIsEshopRychleLastOrdersNotificationModalOpen(true);
          }
          if (result.type === "shoptetCouponCode") {
            setIsShoptetCouponNotificationModalOpen(true);
          }
          if (
            result.type === "spolehlivaRecenzeShopRating" ||
            result.type === "spolehlivaRecenzeProductRating"
          ) {
            setIsSpolehliveRecenzeNotificationModalOpen(true);
          }
          if (result.type === "googleReviews") {
            setIsGoogleReviewNotificationModalOpen(true);
          }
          if (result.type === "trustpilotReviews") {
            setIsTrustpilotReviewNotificationModalOpen(true);
          }
        } catch (e: any) {
          dispatch(
            showFlashMessageWithTimeout(dispatch, e.toString(), "error"),
          );
        }
      }
    };

    fetchNotification();
  }, [notificationId, dispatch]);

  if (!notificationId) {
    return (
      <Typography style={{ marginTop: 20, marginLeft: 20 }}>
        Nepodařilo se načíst nastavení
      </Typography>
    );
  }

  return (
    <Box>
      {notificationToEdit && (
        <>
          {ordersAndRegistrationsModalOpenFor !== undefined && (
            <OrdersAndRegistrationsModal
              data={notificationToEdit}
              createdNotificationId={notificationId}
              forOrders={ordersAndRegistrationsModalOpenFor === "lastOrders"}
              isOpen={!!ordersAndRegistrationsModalOpenFor}
              branding={{
                brandingIsShowingIcon: showIcon,
                activeSize,
                backgroundColor,
                backgroundOpacity,
                borderRadius,
                iconBackgroundColor,
                iconBackgroundRadius,
                iconColor,
                textColor,
              }}
              onClose={() => {
                setOrdersAndRegistrationsModalOpenFor(undefined);
                navigate(-1);
              }}
              language={language}
            />
          )}
          <ActualVisitorsModal
            data={notificationToEdit}
            createdNotificationId={notificationId}
            isOpen={isActualVisitorsModalOpen}
            branding={{
              brandingIsShowingIcon: showIcon,
              activeSize,
              backgroundColor,
              backgroundOpacity,
              borderRadius,
              iconBackgroundColor,
              iconBackgroundRadius,
              iconColor,
              textColor,
            }}
            onClose={() => {
              setIsActualVisitorsModalOpen(false);
              navigate(-1);
            }}
            language={language}
          />
          <SummaryModal
            data={notificationToEdit}
            createdNotificationId={notificationId}
            isOpen={!!summaryModalOpenedFor}
            onClose={() => {
              setSummaryModalOpenedFor(undefined);
              navigate(-1);
            }}
            branding={{
              brandingIsShowingIcon: showIcon,
              activeSize,
              backgroundColor,
              backgroundOpacity,
              borderRadius,
              iconBackgroundColor,
              iconBackgroundRadius,
              iconColor,
              textColor,
            }}
            language={language}
          />
          <HeurekaNotificationModal
            data={notificationToEdit}
            branding={{
              brandingIsShowingIcon: showIcon,
              activeSize,
              backgroundColor,
              backgroundOpacity,
              borderRadius,
              iconBackgroundColor,
              iconBackgroundRadius,
              iconColor,
              textColor,
            }}
            createdNotificationId={notificationId}
            isOpen={isHeurekaNotificationModalOpen}
            forOrders={ordersAndRegistrationsModalOpenFor === "lastOrders"}
            onClose={() => {
              setIsHeurekaNotificationModalOpen(false);
              navigate(-1);
            }}
          />
          <SpolehliveRecenzeNotificationModal
            data={notificationToEdit}
            createdNotificationId={notificationId}
            isOpen={isSpolehliveRecenzeNotificationModalOpen}
            onClose={() => {
              setIsSpolehliveRecenzeNotificationModalOpen(false);
              navigate(-1);
            }}
          />
          <ZboziNotificationModal
            data={notificationToEdit}
            branding={{
              brandingIsShowingIcon: showIcon,
              activeSize,
              backgroundColor,
              backgroundOpacity,
              borderRadius,
              iconBackgroundColor,
              iconBackgroundRadius,
              iconColor,
              textColor,
            }}
            forOrders={ordersAndRegistrationsModalOpenFor === "lastOrders"}
            createdNotificationId={notificationId}
            isOpen={isZboziNotificationModalOpen}
            onClose={() => {
              setIsZboziNotificationModalOpen(false);
              navigate(-1);
            }}
          />
          <CustomNotificationModal
            data={notificationToEdit}
            createdNotificationId={notificationId}
            isOpen={isCustomNotificationModalOpen}
            onClose={() => {
              setIsCustomNotificationModalOpen(false);
              navigate(-1);
            }}
            branding={{
              brandingIsShowingIcon: showIcon,
              activeSize,
              backgroundColor,
              backgroundOpacity,
              borderRadius,
              iconBackgroundColor,
              iconBackgroundRadius,
              iconColor,
              textColor,
              logoDisplay: customWebBranding?.logoDisplay,
              logo: customWebBranding?.logo,
              logoBackgroundColor: customWebBranding?.logoBackgroundColor,
              logoText: customWebBranding?.logoText,
            }}
          />
          <ShoptetLastOrdersNotificationModal
            data={notificationToEdit}
            createdNotificationId={notificationId}
            isOpen={isShoptetLastOrdersNotificationModalOpen}
            onClose={() => {
              setIsShoptetLastOrdersNotificationModalOpen(false);
              navigate(-1);
            }}
            branding={{
              brandingIsShowingIcon: showIcon,
              activeSize,
              backgroundColor,
              backgroundOpacity,
              borderRadius,
              iconBackgroundColor,
              iconBackgroundRadius,
              iconColor,
              textColor,
            }}
            language={language}
          />
          <EshopRychleLastOrdersNotificationModal
            data={notificationToEdit}
            createdNotificationId={notificationId}
            isOpen={isEshopRychleLastOrdersNotificationModalOpen}
            onClose={() => {
              setIsEshopRychleLastOrdersNotificationModalOpen(false);
              navigate(-1);
            }}
            branding={{
              brandingIsShowingIcon: showIcon,
              activeSize,
              backgroundColor,
              backgroundOpacity,
              borderRadius,
              iconBackgroundColor,
              iconBackgroundRadius,
              iconColor,
              textColor,
            }}
            language={language}
          />
          <ShoptetCouponCodeNotificationModal
            data={notificationToEdit}
            createdNotificationId={notificationId}
            isOpen={isShoptetCouponNotificationModalOpen}
            onClose={() => {
              setIsShoptetCouponNotificationModalOpen(false);
              navigate(-1);
            }}
            branding={{
              brandingIsShowingIcon: showIcon,
              activeSize,
              backgroundColor,
              backgroundOpacity,
              borderRadius,
              iconBackgroundColor,
              iconBackgroundRadius,
              iconColor,
              textColor,
            }}
            language={language}
          />
          <GoogleReviewNotificationModal
            data={notificationToEdit}
            createdNotificationId={notificationId}
            isOpen={isGoogleReviewNotificationModalOpen}
            onClose={() => {
              setIsGoogleReviewNotificationModalOpen(false);
              navigate(-1);
            }}
          />
          <TrustPilotNotificationModal
            data={notificationToEdit}
            createdNotificationId={notificationId}
            isOpen={isTrustpilotReviewNotificationModalOpen}
            onClose={() => {
              setIsTrustpilotReviewNotificationModalOpen(false);
              navigate(-1);
            }}
          />
        </>
      )}
    </Box>
  );
};

export default EditNotification;
